/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSigintSignalsRequest,
  ListSigintSignalsSecurity,
} from "../models/operations/listsigintsignals.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSigintSignalsInfiniteQuery,
  buildSigintSignalsQuery,
  prefetchSigintSignals,
  prefetchSigintSignalsInfinite,
  queryKeySigintSignals,
  queryKeySigintSignalsInfinite,
  SigintSignalsInfiniteQueryData,
  SigintSignalsPageParams,
  SigintSignalsQueryData,
} from "./sigintSignals.core.js";
export {
  buildSigintSignalsInfiniteQuery,
  buildSigintSignalsQuery,
  prefetchSigintSignals,
  prefetchSigintSignalsInfinite,
  queryKeySigintSignals,
  queryKeySigintSignalsInfinite,
  type SigintSignalsInfiniteQueryData,
  type SigintSignalsPageParams,
  type SigintSignalsQueryData,
};

export type SigintSignalsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSignals sigint
 *
 * @remarks
 * List active custom signals in deterministic ID order. The cursor is the final ID from the previous page.
 */
export function useSigintSignals(
  request?: ListSigintSignalsRequest | undefined,
  security?: ListSigintSignalsSecurity | undefined,
  options?: QueryHookOptions<SigintSignalsQueryData, SigintSignalsQueryError>,
): UseQueryResult<SigintSignalsQueryData, SigintSignalsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSigintSignalsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSignals sigint
 *
 * @remarks
 * List active custom signals in deterministic ID order. The cursor is the final ID from the previous page.
 */
export function useSigintSignalsSuspense(
  request?: ListSigintSignalsRequest | undefined,
  security?: ListSigintSignalsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SigintSignalsQueryData,
    SigintSignalsQueryError
  >,
): UseSuspenseQueryResult<SigintSignalsQueryData, SigintSignalsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSigintSignalsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSignals sigint
 *
 * @remarks
 * List active custom signals in deterministic ID order. The cursor is the final ID from the previous page.
 */
export function useSigintSignalsInfinite(
  request?: ListSigintSignalsRequest | undefined,
  security?: ListSigintSignalsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SigintSignalsInfiniteQueryData,
    SigintSignalsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<SigintSignalsInfiniteQueryData, SigintSignalsPageParams>,
  SigintSignalsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SigintSignalsInfiniteQueryData,
    SigintSignalsQueryError,
    InfiniteData<SigintSignalsInfiniteQueryData, SigintSignalsPageParams>,
    QueryKey,
    SigintSignalsPageParams
  >({
    ...buildSigintSignalsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listSignals sigint
 *
 * @remarks
 * List active custom signals in deterministic ID order. The cursor is the final ID from the previous page.
 */
export function useSigintSignalsInfiniteSuspense(
  request?: ListSigintSignalsRequest | undefined,
  security?: ListSigintSignalsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SigintSignalsInfiniteQueryData,
    SigintSignalsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<SigintSignalsInfiniteQueryData, SigintSignalsPageParams>,
  SigintSignalsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SigintSignalsInfiniteQueryData,
    SigintSignalsQueryError,
    InfiniteData<SigintSignalsInfiniteQueryData, SigintSignalsPageParams>,
    QueryKey,
    SigintSignalsPageParams
  >({
    ...buildSigintSignalsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSigintSignalsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SigintSignalsQueryData,
): SigintSignalsQueryData | undefined {
  const key = queryKeySigintSignals(...queryKeyBase);

  return client.setQueryData<SigintSignalsQueryData>(key, data);
}

export function invalidateSigintSignals(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "sigint", "listSignals", ...queryKeyBase],
  });
}

export function invalidateAllSigintSignals(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "sigint", "listSignals"],
  });
}
